import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';

/** Height of the part of the screen the software keyboard leaves visible. */
const HEIGHT_VARIABLE = '--pg-visual-height';

/** How far the keyboard pushed the page up, which `position: fixed` ignores. */
const OFFSET_VARIABLE = '--pg-visual-offset';

/** Set on `<html>` while the keyboard is up, so dialogs can stop centring. */
const KEYBOARD_CLASS = 'pg-keyboard-open';

/** Below this, a shrunken visual viewport is chrome, not a keyboard. */
const KEYBOARD_THRESHOLD_PX = 120;

/**
 * How long Safari keeps animating after it reports the keyboard. It scrolls
 * the page to the focused field in its own process and tells the page only
 * the final numbers, part of the way in; a dialog moved before the animation
 * ends lands mid-flight and jumps — measured on the simulator (POC-9506).
 */
const SETTLE_MS = 200;

/**
 * Publishes the visual viewport to CSS.
 *
 * `dvh` follows the browser's own chrome, not the software keyboard: on iOS
 * the keyboard leaves about 512pt of a 956pt screen and `100dvh` still reads
 * 956. A dialog capped at `90dvh` is therefore taller than the room it has,
 * and its buttons sit under the keys — measured on the simulator with Add
 * transaction, where Cancel and Save were unreachable while the amount field
 * was focused.
 *
 * So the height goes into a variable that `.pg-dialog-height` reads, and while
 * the keyboard is up the class pins overlays to the top of the screen instead
 * of centring them in a viewport half of which is covered.
 *
 * The offset matters for the detail drawers: they are `fixedInViewport`, so
 * `top: 0` means the top of the *layout* viewport, which the keyboard has
 * pushed above the screen — the drawer's close button went with it and no
 * amount of scrolling brought it back, because the page scroll is blocked
 * while a drawer is open.
 *
 * Same source as `KeyboardAwareBlockScrollStrategy`, which already reads
 * `visualViewport` to undo the scroll offset the keyboard leaves behind.
 */
export function provideVisualViewportHeight(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => trackVisualViewport(inject(DOCUMENT))),
  ]);
}

/**
 * The initializer's body, callable on its own so the publishing can be tested
 * without an application: what it writes is what the stylesheet reads, and a
 * variable that stops being published turns its CSS rule into a no-op.
 */
export function trackVisualViewport(document: Document): void {
  const window = document.defaultView;
  const viewport = window?.visualViewport;

  if (!window || !viewport) return;

  const root = document.documentElement;

  // The baseline is the tallest viewport seen, not `window.innerHeight`:
  // on iOS Safari that shrinks with the keyboard too, so the difference
  // stays near zero and the keyboard goes unnoticed — measured on the
  // simulator, where the dialog clamp worked (it reads the height) while
  // the drawer rule never fired (it reads this class).
  let tallest = viewport.height;

  const publish = () => {
    tallest = Math.max(tallest, viewport.height);

    root.style.setProperty(HEIGHT_VARIABLE, `${viewport.height}px`);
    root.style.setProperty(OFFSET_VARIABLE, `${viewport.offsetTop}px`);
    root.classList.toggle(
      KEYBOARD_CLASS,
      tallest - viewport.height > KEYBOARD_THRESHOLD_PX
    );
  };

  // A field can also grow after it was revealed: its error appears while the
  // keyboard is up — "111." in the goal form adds a 19px line, 11px of which
  // went under the footer. So while the keyboard is up the focused field is
  // watched, and revealed again whenever its box changes.
  const growth = window.ResizeObserver
    ? new window.ResizeObserver(() => revealFocusedField(document))
    : null;
  let watched: HTMLElement | null = null;

  const watchFocusedField = () => {
    const field = root.classList.contains(KEYBOARD_CLASS)
      ? focusedField(document)
      : null;
    if (field === watched) return;

    if (watched) growth?.unobserve(watched);
    watched = field;
    if (field) growth?.observe(field);
  };

  // POC-9506. While the keyboard comes, goes or moves the page between
  // fields, Safari animates on its own and the dialogs ride along; the layout
  // for the keyboard waits until that is over and then glides into place.
  // Everything else — the browser's own bars, a desktop window — applies at
  // once, as before.
  let settling: ReturnType<typeof setTimeout> | undefined;
  let glide: Glide | null = null;
  // Only a change of size brings the field back into view: the page panning
  // under a keyboard that is already up leaves the dialog's scroll alone
  let resized = false;

  const update = (event: Event) => {
    clearTimeout(settling);
    resized ||= event.type === 'resize';

    const keyboard =
      Math.max(tallest, viewport.height) - viewport.height >
      KEYBOARD_THRESHOLD_PX;
    if (!keyboard && !root.classList.contains(KEYBOARD_CLASS)) {
      publish();
      resized = false;
      return;
    }

    const settle = () => {
      glide?.stop();
      glide = glideDialogs(document, publish, resized);
      resized = false;
      watchFocusedField();
    };

    // A closing keyboard reports itself once its animation is over
    if (keyboard) settling = setTimeout(settle, SETTLE_MS);
    else settle();
  };

  publish();
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  document.addEventListener('focusin', watchFocusedField);

  // A rotation changes what "tallest" means.
  window.addEventListener('orientationchange', () => {
    tallest = viewport.height;
    publish();
  });
}

/** Room left between a revealed field and the edge it was brought to. */
const REVEAL_MARGIN_PX = 8;

/** Fields the software keyboard opens for; a readonly one opens a picker. */
const TEXT_ENTRY = [
  'input:not([readonly]):not([type="checkbox"]):not([type="radio"])' +
    ':not([type="button"]):not([type="submit"]):not([type="range"])',
  'textarea:not([readonly])',
  '[contenteditable="true"]',
].join(', ');

/** How long a dialog takes to reach the room the keyboard leaves it. */
const GLIDE_MS = 250;

interface Glide {
  stop(): void;
}

/**
 * POC-9506. Applies `change` — the layout for the keyboard — so that nothing
 * jumps: each dialog starts from where it is on the screen and travels to its
 * new place, its card resizing on the way. With `reveal`, the field being
 * typed into comes into view (POC-8786) within the same motion, not after it.
 */
function glideDialogs(
  document: Document,
  change: () => void,
  reveal: boolean
): Glide {
  const window = document.defaultView as Window;
  const field = focusedField(document);

  const dialogs = Array.from(
    document.querySelectorAll<HTMLElement>(
      '.cdk-global-overlay-wrapper > .cdk-overlay-pane'
    )
  ).map((pane) => {
    // What changes height: the card the dialog caps itself with, or else the
    // pane, capped by the wrapper
    const card = pane.querySelector<HTMLElement>('.pg-dialog-height');
    const box = card ?? pane;
    const own = field && pane.contains(field) ? field : null;

    return {
      pane,
      card,
      box,
      field: own,
      top: pane.getBoundingClientRect().top,
      height: box.getBoundingClientRect().height,
    };
  });

  // What is measured next is the layout itself, not a glide in progress
  for (const { pane, box } of dialogs) {
    pane.style.translate = '';
    pane.style.maxHeight = '';
    box.style.maxHeight = '';
  }
  change();

  // Revealing is for a keyboard that is up, not one that just went away
  const revealing =
    reveal && document.documentElement.classList.contains(KEYBOARD_CLASS);
  // A field in an overlay that is not a dialog has nothing to glide with
  if (revealing && field && !dialogs.some((dialog) => dialog.field)) {
    revealFocusedField(document);
  }

  const moves = dialogs.map((dialog) => {
    const area = revealing && dialog.field ? scrollArea(dialog.field) : null;
    const from = area?.scrollTop ?? 0;
    let to = from;

    if (dialog.field && area) {
      const box = area.getBoundingClientRect();
      const rect = dialog.field.getBoundingClientRect();
      const wanted = keptInView(rect.top, rect.height, box);
      to = Math.min(
        Math.max(from + (rect.top - wanted), 0),
        area.scrollHeight - area.clientHeight
      );
    }

    return {
      ...dialog,
      area,
      from,
      to,
      final: dialog.pane.getBoundingClientRect().top,
      newHeight: dialog.box.getBoundingClientRect().height,
      offset: 0,
    };
  });

  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  let start: number | null = null;
  let frame = 0;

  // The clock starts with the first frame drawn, not with this call: the
  // page can be busy for a while after a layout change — 44ms once, on the
  // simulator — and a clock already running then makes the first visible
  // step a jump. It is read here, too, rather than taken from
  // requestAnimationFrame, whose timestamp on iOS came 71ms before the start.
  const step = (drawn: boolean) => {
    const now = window.performance.now();
    if (drawn && start === null) start = now;

    const progress = still ? 1 : Math.min(1, (now - (start ?? now)) / GLIDE_MS);
    const eased = (1 - Math.cos(Math.PI * progress)) / 2;
    const moving = progress < 1;

    for (const move of moves) {
      if (move.height !== move.newHeight) {
        move.box.style.maxHeight = moving
          ? `${move.height + (move.newHeight - move.height) * eased}px`
          : '';
        // The wrapper's cap on the pane has already jumped; the card's glide
        // must not be cut by it
        if (move.card) move.pane.style.maxHeight = moving ? 'none' : '';
      }
      if (move.area) {
        move.area.scrollTop = move.from + (move.to - move.from) * eased;
      }
    }

    // A centred dialog moves with its own height, so the place it is laid out
    // at is read each frame and the transform makes up the rest of the way
    for (const move of moves) {
      const laidOut = move.pane.getBoundingClientRect().top - move.offset;
      const wanted = move.top + (move.final - move.top) * eased;
      move.offset = moving ? wanted - laidOut : 0;
      move.pane.style.translate = moving ? `0 ${move.offset}px` : '';
    }

    if (moving) frame = window.requestAnimationFrame(() => step(true));
  };
  step(false);

  return { stop: () => window.cancelAnimationFrame(frame) };
}

/**
 * POC-8786. Where a field at `top` should stand in `box`: a field already in
 * view stays where it is; one that is not comes in with some room, since flush
 * against the footer its border read as cut on iOS. A field taller than the
 * area keeps its top in view.
 */
function keptInView(top: number, height: number, box: DOMRect): number {
  if (top + height > box.bottom) {
    return Math.max(box.bottom - REVEAL_MARGIN_PX - height, box.top);
  }
  if (top < box.top) return box.top + REVEAL_MARGIN_PX;

  return top;
}

/**
 * POC-8786. The keyboard shrinks a `.pg-dialog-height` dialog *after* the
 * field took focus: the browser had scrolled the field into the tall dialog,
 * then the dialog lost half its height and the field went under the new
 * bottom edge — QA's recording on iOS, and in the browser pane with the
 * viewport cut from 812 to 420: the scroll area ended at 306 while the
 * focused input sat at 635–659.
 *
 * Only the dialog's own scroll area moves, never the page: the block scroll
 * strategy pins the page, and the visual viewport is the browser's to pan.
 */
function revealFocusedField(document: Document): void {
  const target = focusedField(document);
  if (!target) return;

  const area = scrollArea(target);
  if (!area) return;

  const box = area.getBoundingClientRect();
  const rect = target.getBoundingClientRect();

  area.scrollTop += rect.top - keptInView(rect.top, rect.height, box);
}

/** The text field being typed into inside an overlay, with its label and message. */
function focusedField(document: Document): HTMLElement | null {
  const field = document.activeElement;
  if (!(field instanceof HTMLElement) || !field.matches(TEXT_ENTRY))
    return null;
  if (!field.closest('.cdk-overlay-container')) return null;

  return field.closest<HTMLElement>('.mat-mdc-form-field') ?? field;
}

/** The nearest ancestor inside the overlay that actually scrolls. */
function scrollArea(element: HTMLElement): HTMLElement | null {
  for (
    let node = element.parentElement;
    node && !node.classList.contains('cdk-overlay-container');
    node = node.parentElement
  ) {
    const { overflowY } = getComputedStyle(node);
    const scrolls = overflowY === 'auto' || overflowY === 'scroll';

    if (scrolls && node.scrollHeight > node.clientHeight) return node;
  }

  return null;
}
